import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { META_PIXEL_ID, metaPixel, captureClickId } from '../utils/pixel';

/**
 * Carrega o script fbevents.js dinamicamente, apenas após consentimento.
 * Dispara PageView em cada navegação SPA (react-router-dom).
 *
 * Uso: <MetaPixelLoader hasConsent={consent === 'granted'} />
 */
export default function MetaPixelLoader({ hasConsent }: { hasConsent: boolean }) {
  const location = useLocation();

  // Carrega o script quando consentimento é dado pela primeira vez
  useEffect(() => {
    if (!hasConsent) return;

    // Antes de tudo: gravar o `_fbc` a partir do `?fbclid=`. Se o utilizador
    // for redireccionado a meio do fluxo, a atribuição já está guardada.
    captureClickId();

    if (document.getElementById('meta-pixel-script')) {
      // Já carregado — só garantir que o estado interno está actualizado
      metaPixel.markLoaded();
      metaPixel.grantConsent();
      return;
    }

    // Injector fbq stub + script externo
    const w = window as any;
    if (!w.fbq) {
      const n: any = (w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      });
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
    }

    w.fbq('consent', 'grant');
    w.fbq('init', META_PIXEL_ID);
    // Sem `fbq('track','PageView')` aqui: o efeito de rota abaixo já dispara
    // um PageView ao montar, e este dispararia um segundo sem event_id —
    // inflacionando a métrica e escapando à deduplicação com a CAPI.

    const script = document.createElement('script');
    script.id = 'meta-pixel-script';
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.async = true;
    script.onload = () => {
      metaPixel.markLoaded();
      metaPixel.grantConsent();
    };
    document.head.appendChild(script);
  }, [hasConsent]);

  // PageView em cada mudança de rota (SPA navigation)
  useEffect(() => {
    if (!hasConsent) return;
    metaPixel.pageView();
  }, [location.pathname, hasConsent]);

  return null;
}
